import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import PortalLayout from "../../components/PortalLayout";

const STAGE_LABELS = {
  invited: "Invitati",
  activated: "Attivati",
  kyc_complete: "KYC completato",
  subscribed: "Prima sottoscrizione",
};

const STAGE_HINTS = {
  invited: "Inviti generati (outreach)",
  activated: "Hanno accettato l'invito e attivato l'account",
  kyc_complete: "6/6 documenti KYC approvati",
  subscribed: "Almeno una richiesta di sottoscrizione inviata",
};

const STAGE_COLORS = {
  invited: "rgba(167,128,73,0.30)",
  activated: "rgba(167,128,73,0.50)",
  kyc_complete: "rgba(167,128,73,0.72)",
  subscribed: "var(--br-bronze)",
};

const STAGE_PILL = {
  invited: { label: "Invitato", color: "var(--br-text-dim)" },
  activated: { label: "Attivato", color: "rgba(167,128,73,0.85)" },
  kyc_complete: { label: "KYC ok", color: "#c79a58" },
  subscribed: { label: "Sottoscritto", color: "#5f8a6b" },
};

export default function AdminOnboardingFunnelPage() {
  const { locale } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/onboarding-funnel");
      setData(res.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore di caricamento.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const maxCount = useMemo(() => {
    if (!data?.stages?.length) return 1;
    return Math.max(...data.stages.map((s) => s.count), 1);
  }, [data]);

  return (
    <PortalLayout>
      <div className="max-w-[1100px] mx-auto px-6 py-14" data-testid="admin-funnel-page">
        <div className="br-overline">Analytics</div>
        <h1 className="font-serif-display text-4xl mt-3 text-[color:var(--br-parchment)]">
          Funnel di onboarding
        </h1>
        <p className="text-[0.9rem] text-[color:var(--br-text-dim)] mt-3 max-w-[660px] leading-relaxed">
          Conversione dei membri lungo l'intero ciclo: invito, attivazione, KYC completo e prima
          richiesta di sottoscrizione. La tabella evidenzia dove ogni cliente si è fermato.
        </p>

        {error ? (
          <div
            className="mt-6 br-card px-6 py-4 text-[0.9rem]"
            style={{ borderLeft: "2px solid #b36b6b", color: "var(--br-parchment)" }}
            data-testid="funnel-error"
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-10 text-[0.9rem] text-[color:var(--br-text-dim)]" data-testid="funnel-loading">
            Caricamento…
          </div>
        ) : data ? (
          <>
            {/* Funnel bars */}
            <div className="mt-10 br-card px-8 py-8" data-testid="funnel-stages">
              <div className="space-y-6">
                {data.stages.map((s) => {
                  const width = Math.max((s.count / maxCount) * 100, s.count > 0 ? 6 : 2);
                  return (
                    <div key={s.key} data-testid={`funnel-stage-${s.key}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <div>
                          <span className="text-[0.95rem] text-[color:var(--br-parchment)]">
                            {STAGE_LABELS[s.key]}
                          </span>
                          <span className="text-[0.72rem] text-[color:var(--br-text-dim)] ml-3">
                            {STAGE_HINTS[s.key]}
                          </span>
                        </div>
                        <span
                          className="font-mono-fin text-[1.1rem] text-[color:var(--br-parchment)]"
                          data-testid={`funnel-count-${s.key}`}
                        >
                          {s.count}
                        </span>
                      </div>
                      <div
                        className="h-3 rounded-full overflow-hidden"
                        style={{ background: "var(--br-line)" }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${width}%`,
                            background: STAGE_COLORS[s.key],
                            transition: "width 0.6s ease",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Conversion rates */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="funnel-conversion">
              <ConversionCard label="Invito → Attivazione" value={data.conversion.invite_to_activation} testid="conv-invite-activation" />
              <ConversionCard label="Attivazione → KYC" value={data.conversion.activation_to_kyc} testid="conv-activation-kyc" />
              <ConversionCard label="KYC → Sottoscrizione" value={data.conversion.kyc_to_subscription} testid="conv-kyc-subscription" />
              <ConversionCard label="Conversione totale" value={data.conversion.overall} testid="conv-overall" highlight />
            </div>

            <div className="mt-4 text-[0.78rem] text-[color:var(--br-text-dim)]" data-testid="funnel-meta">
              {data.total_clients} clienti · {data.pending_invites} inviti in attesa di attivazione
            </div>

            {/* Per-client breakdown */}
            <div className="mt-10">
              <div className="br-overline mb-4">Dettaglio clienti</div>
              {data.clients.length === 0 ? (
                <div
                  className="br-card px-8 py-12 text-center text-[0.9rem] text-[color:var(--br-text-dim)]"
                  data-testid="funnel-clients-empty"
                >
                  Nessun cliente ancora.
                </div>
              ) : (
                <div className="br-card overflow-x-auto" data-testid="funnel-clients-table">
                  <table className="w-full text-left text-[0.85rem]">
                    <thead>
                      <tr className="text-[color:var(--br-text-dim)]" style={{ borderBottom: "1px solid var(--br-line)" }}>
                        <th className="px-6 py-4 font-normal">Cliente</th>
                        <th className="px-6 py-4 font-normal">Attivato</th>
                        <th className="px-6 py-4 font-normal">KYC</th>
                        <th className="px-6 py-4 font-normal">Sottoscrizione</th>
                        <th className="px-6 py-4 font-normal">Stadio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.clients.map((c) => {
                        const pill = STAGE_PILL[c.stage] || STAGE_PILL.invited;
                        return (
                          <tr
                            key={c.id}
                            style={{ borderBottom: "1px solid var(--br-line)" }}
                            data-testid={`funnel-client-${c.id}`}
                          >
                            <td className="px-6 py-4">
                              <div className="text-[color:var(--br-parchment)]">{c.name || "—"}</div>
                              <div className="text-[0.75rem] text-[color:var(--br-text-dim)]">{c.email}</div>
                            </td>
                            <td className="px-6 py-4 text-[color:var(--br-text-dim)]">
                              {c.activated ? formatDate(c.activated_at, locale) : "—"}
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-mono-fin text-[color:var(--br-parchment)]">
                                {c.kyc_approved}/{c.kyc_total}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-[color:var(--br-text-dim)]">
                              {c.has_subscription
                                ? c.subscription_approved
                                  ? "Approvata"
                                  : "In attesa"
                                : "—"}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className="font-mono-fin text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full whitespace-nowrap"
                                style={{ color: pill.color, border: `1px solid ${pill.color}` }}
                                data-testid={`funnel-client-stage-${c.id}`}
                              >
                                {pill.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </PortalLayout>
  );
}

function ConversionCard({ label, value, testid, highlight }) {
  return (
    <div
      className="br-card px-6 py-5"
      style={highlight ? { borderLeft: "2px solid var(--br-bronze)" } : undefined}
      data-testid={testid}
    >
      <div className="text-[0.7rem] tracking-[0.14em] uppercase text-[color:var(--br-text-dim)]">
        {label}
      </div>
      <div className="font-mono-fin text-[1.6rem] mt-2 text-[color:var(--br-parchment)]">
        {value}%
      </div>
    </div>
  );
}
